import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { CiClock2, CiLocationOn, CiMail } from 'react-icons/ci';
import { PiPhoneCallThin } from 'react-icons/pi';
import { FaInstagram, FaLinkedinIn, FaSquareXTwitter } from 'react-icons/fa6';
import { RiTiktokLine } from 'react-icons/ri';
import { LuArrowUpToLine } from 'react-icons/lu';
import ScrollReveal from '../components/ScrollReveal';
import { submitNewsletterSubscription } from '../utils/newsletterService';

const footerLinks = [
    { label: 'Virtual Reality', href: '/virtual-reality' },
    { label: 'Augmented Reality', href: '/augmented-reality' },
    { label: 'Syteca Cybersecurity', href: 'https://www.syteca.com/en', external: true },
    { label: 'Enterprise Technology Solutions', href: '/enterprise-technology-solutions' },
    { label: 'Healthcare Solutions', href: '/healthcare-solutions' },
    { label: 'Education Technology Solutions', href: '/education-technology-solutions' },
    { label: 'Financial Technology Solutions', href: '/financial-technology-solutions' },
    { label: 'Systems Integration', href: '/systems-integration' },
];

const quickLinks = [
    { label: 'FAQs', href: '/faqs' },
    { label: 'VRIFY Tours', href: 'https://vrifyafrica.com/virtual-tour', external: true },
    { label: 'Contact Us', href: '/contactus' },
    { label: 'About Us', href: '/aboutus' }
];

const socialLinks = [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/vrify-technologies', icon: FaLinkedinIn },
    { label: 'X (formerly Twitter)', href: 'https://x.com/VrifyTech?t=WcnaecQOz1HMkjra3Bpd7A&s=08', icon: FaSquareXTwitter },
    { label: 'Instagram', href: 'https://www.instagram.com/vrifytechnologies?igsh=MTdpbWl4eGFibjVmbA=', icon: FaInstagram },
    { label: 'TikTok', href: 'https://www.tiktok.com/@vrifytechnologies?_t=ZM-90BY6Oc2wHr&_r=1', icon: RiTiktokLine }
];

const Footer = () => {
    const [showScroll, setShowScroll] = useState(false);
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterWebsite, setNewsletterWebsite] = useState('');
    const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
    const [newsletterStatus, setNewsletterStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        const handleScroll = () => setShowScroll(window.scrollY > 220);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNewsletterSubmit = async (event) => {
        event.preventDefault();
        setNewsletterStatus({ type: '', message: '' });

        if (!newsletterEmail.trim()) {
            setNewsletterStatus({ type: 'error', message: 'Please enter your email address.' });
            return;
        }

        setNewsletterSubmitting(true);
        try {
            await submitNewsletterSubscription(newsletterEmail, newsletterWebsite);
            setNewsletterEmail('');
            setNewsletterWebsite('');
            setNewsletterStatus({ type: 'success', message: 'Thanks for subscribing. We will keep you updated.' });
        } catch (error) {
            console.error(error);
            setNewsletterStatus({ type: 'error', message: error?.message || 'We could not submit your email. Please try again.' });
        } finally {
            setNewsletterSubmitting(false);
        }
    };

    return (
        <footer className='Footer font-nanum-myeongjo cursor-pointer'>
            <div className='relative overflow-hidden bg-slate-950 text-white'>
                <div className='pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/10 via-white/5 to-transparent blur-md' />

                <ScrollReveal animationClass='animate-fade-in' threshold={0.2} delay={200}>
                    <div className='mx-auto px-4 pb-6 pt-8 sm:px-6 md:px-10 lg:px-20 xl:px-40'>
                        <div className='rounded-[28px] border border-white/10 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 px-5 py-6 shadow-[0_24px_80px_rgba(15,23,42,0.45)] sm:px-8 sm:py-8 lg:px-10'>
                            <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
                                <div className='max-w-2xl'>
                                    <p className='mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-yellow-300'>Subscribe to our updates</p>
                                    <h2 className='text-2xl font-bold text-white sm:text-3xl'>Get the latest updates</h2>
                                    <p className='mt-3 text-sm leading-7 text-slate-300 sm:text-base'>
                                        Get latest updates, product launches, and exclusive insights from VRIFY Technologies.
                                    </p>
                                </div>

                                <form
                                    onSubmit={handleNewsletterSubmit}
                                    className='relative w-full max-w-xl rounded-[24px] border border-white/10 bg-white/5 p-3 sm:p-4'
                                >
                                    <div className='absolute -left-[10000px] h-px w-px overflow-hidden' aria-hidden='true'>
                                        <label htmlFor='newsletter-website'>Website</label>
                                        <input
                                            id='newsletter-website'
                                            type='text'
                                            name='website'
                                            value={newsletterWebsite}
                                            onChange={(event) => setNewsletterWebsite(event.target.value)}
                                            tabIndex='-1'
                                            autoComplete='off'
                                        />
                                    </div>
                                    <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                                        <label htmlFor='email-footer' className='sr-only'>Email address</label>
                                        <div className='flex flex-1 items-center gap-3 rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 transition focus-within:border-yellow-300'>
                                            <CiMail className='shrink-0 text-lg text-yellow-300' />
                                            <input
                                                id='email-footer'
                                                type='email'
                                                name='email'
                                                placeholder='Enter your email address'
                                                value={newsletterEmail}
                                                onChange={(event) => setNewsletterEmail(event.target.value)}
                                                maxLength={254}
                                                required
                                                className='w-full bg-transparent text-sm text-white placeholder:text-slate-400 outline-none'
                                            />
                                        </div>

                                        <button
                                            type='submit'
                                            disabled={newsletterSubmitting}
                                            className='inline-flex items-center justify-center rounded-2xl bg-yellow-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-200 sm:min-w-[140px]'
                                        >
                                            {newsletterSubmitting ? 'Submitting...' : 'Subscribe'}
                                        </button>
                                    </div>
                                    {newsletterStatus.message && (
                                        <p className={`mt-3 text-xs ${newsletterStatus.type === 'success' ? 'text-emerald-300' : 'text-rose-300'}`}>
                                            {newsletterStatus.message}
                                        </p>
                                    )}
                                    <p className='mt-3 text-xs text-slate-300'>We send helpful updates, launches, and offers — no spam.</p>
                                </form>
                            </div>
                        </div>
                    </div>
                </ScrollReveal>

                <ScrollReveal animationClass='animate-fade-in' threshold={0.2} delay={350}>
                    <div className='mx-auto grid gap-8 px-4 pb-8 pt-4 sm:px-6 md:px-10 md:grid-cols-2 lg:grid-cols-[1.1fr_0.8fr_0.9fr_0.8fr_1fr] lg:px-20 xl:px-40'>
                        <div className='flex flex-col gap-4'>
                            <Link to='/' className='inline-flex w-fit'>
                                <img src='../assets/newvrifylogo1.png' alt='VRIFY Technologies' className='h-28 w-72 object-cover sm:h-24' />
                            </Link>
                            <p className='max-w-sm text-sm leading-7 text-slate-300'>
                                VRIFY TECHNOLOGIES - SEE MORE, BE MORE...
                            </p>
                        </div>

                        <div>
                            <h3 className='mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-white'>Our Solutions</h3>
                            <ul className='space-y-2 text-sm text-slate-300'>
                                {footerLinks.map((item) => (
                                    <li key={item.label}>
                                        {item.external ? (
                                            <a
                                                href={item.href}
                                                target='_blank'
                                                rel='noreferrer noopener'
                                                className='transition hover:text-yellow-300'
                                            >
                                                {item.label}
                                            </a>
                                        ) : (
                                            <Link to={item.href} className='transition hover:text-yellow-300'>
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className='mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-white'>Quick Links</h3>
                            <ul className='space-y-2 text-sm text-slate-300'>
                                {quickLinks.map((item) => (
                                    <li key={item.label}>
                                        {item.external ? (
                                            <a
                                                href={item.href}
                                                target='_blank'
                                                rel='noreferrer noopener'
                                                className='transition hover:text-yellow-300'
                                            >
                                                {item.label}
                                            </a>
                                        ) : (
                                            <Link to={item.href} className='transition hover:text-yellow-300'>
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className='mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-white'>Address</h3>
                            <ul className='space-y-4 text-sm text-slate-300'>
                                <li className='flex gap-3'>
                                    <Tooltip title='Physical Address' arrow>
                                        <span className='mt-0.5 text-yellow-300'><CiLocationOn className='text-lg' /></span>
                                    </Tooltip>
                                    <span className='hover:text-yellow-300 transition'>Nii Sai Road, East Legon, Accra, Ghana.</span>
                                </li>
                                <li className='flex gap-3'>
                                    <Tooltip title='Opening Hours' arrow>
                                        <span className='mt-0.5 text-yellow-300'><CiClock2 className='text-lg' /></span>
                                    </Tooltip>
                                    <div>
                                        <p className='hover:text-yellow-300 transition'>Mon - Fri: 09:00 - 18:00 GMT</p>
                                        <p className='hover:text-yellow-300 transition'>Sat: 09:00 - 18:00 GMT</p>
                                        <p className='hover:text-yellow-300 transition'>Sun: Closed</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className='mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-white'>Contacts</h3>
                            <ul className='space-y-4 text-sm text-slate-300'>
                                <li className='flex gap-3'>
                                    <Tooltip title='Email' arrow>
                                        <span className='mt-0.5 text-yellow-300'><CiMail className='text-lg' /></span>
                                    </Tooltip>
                                    <div>
                                        <p className='hover:text-yellow-300 transition'>info@vrifytechnologies.com</p>
                                        <p className='hover:text-yellow-300 transition'>business@vrifytechnologies.com</p>
                                    </div>
                                </li>
                                <li className='flex gap-3'>
                                    <Tooltip title='Phone' arrow>
                                        <span className='mt-0.5 text-yellow-300'><PiPhoneCallThin className='text-lg' /></span>
                                    </Tooltip>
                                    <div>
                                        <p className='hover:text-yellow-300 transition'>+233553139500</p>
                                        <p className='hover:text-yellow-300 transition'>+233241145862</p>
                                        <p className='hover:text-yellow-300 transition'>+233544847395</p>
                                    </div>
                                </li>
                            </ul>

                            <div className='mt-5'>
                                <p className='mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-white'>Socials</p>
                                <div className='flex flex-wrap gap-3'>
                                    {socialLinks.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <Tooltip key={item.label} title={item.label} arrow>
                                                <a
                                                    href={item.href}
                                                    target='_blank'
                                                    rel='noreferrer noopener'
                                                    className='inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:border-yellow-300 hover:text-yellow-300'
                                                    aria-label={item.label}
                                                >
                                                    <Icon className='text-lg' />
                                                </a>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollReveal>

                <div className='mx-auto px-4 pb-4 sm:px-6 md:px-10 lg:px-20 xl:px-40'>
                    <div className='border-t border-white/10' />
                </div>

                <ScrollReveal animationClass='animate-fade-in' threshold={0.2} delay={450}>
                    <div className='mx-auto flex flex-col gap-3 px-4 pb-6 text-center text-xs text-slate-300 sm:px-6 md:flex-row md:items-center md:justify-between md:px-10 lg:px-20 xl:px-40'>
                        <p className='font-medium text-white'>VRIFY Technologies — See More, Be More. | <span className='hover:text-yellow-600'><a href='/data-processing-policy' target='_blank' rel='noreferrer'>Data Processing Policy</a></span></p>
                        <p>Copyright © {new Date().getFullYear()} VRIFY Technologies. All rights reserved.</p>
                    </div>
                </ScrollReveal>

                {showScroll && (
                    <button
                        onClick={scrollToTop}
                        aria-label='Scroll to top'
                        className='fixed bottom-6 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-yellow-300 text-slate-950 shadow-lg transition hover:bg-yellow-200 sm:right-6'
                    >
                        <LuArrowUpToLine className='text-lg' />
                    </button>
                )}
            </div>
        </footer>
    );
};

export default Footer;
